import { applicationTemplateIds, BID_TEMPLATE_ID, deMinimisEligibleTemplateIds, FDMCA_TEMPLATE_ID, mcaTemplateIds, medicalRecommendationTemplateIds, recordOfDetentionTemplateIds, singleMedicalRecordsTemplateIds, } from "../sections.js";
import { COMPLETED_FORM_STATUSES } from "./baseForm.js";
export function formIsCompleted(form) {
    return COMPLETED_FORM_STATUSES.includes(form.status);
}
export function formIsInProgress(form) {
    return ["in-progress"].includes(form.status);
}
// --- Redo (non-s15) predicates ---------------------------------------------
// A form is in a redo holding state: requested but not yet accepted, or reopened
// for redo. Such a form is still viewable but cannot be used as a basis for, or
// linked to, a downstream form.
export function formIsUnderRedo(form) {
    return (form.redoState === "redo-requested" || form.redoState === "redo-in-progress");
}
// A form that has been superseded by a completed redo. Terminal, watermarked.
export function formIsVoid(form) {
    return form.redoState === "void";
}
// Human-readable label for a form's redo state, or null when it is not under
// redo. Used to show an invalid linked dependency's status for context (e.g. in
// the Linked Forms modal, where such a form is shown but cannot be selected).
// The Record over RedoState is exhaustive: a new redo state will fail to compile
// until a label is added.
const REDO_STATUS_LABELS = {
    void: "Void",
    "redo-requested": "Redo requested",
    "redo-in-progress": "Redo in progress",
};
export function redoStatusLabel(form) {
    return form.redoState ? REDO_STATUS_LABELS[form.redoState] : null;
}
// The single source of truth for "can this form be used as a basis for / linked
// to a downstream form": it must be completed AND not in any redo/void state.
export function formIsValidForProgression(form) {
    return formIsCompleted(form) && form.redoState == null;
}
// A replacement being written for a form under redo. The admission page shows it
// collapsed into the original's row, so any picker that lists in-progress forms
// must hide it too or a single redo produces two rows.
export function formIsCollapsedRedoReplacement(form, forms) {
    return (formIsInProgress(form) &&
        form.redoneFromFormId != null &&
        forms.some((f) => f.id === form.redoneFromFormId && f.redoState === "redo-in-progress"));
}
// Redo permissions (Layer A). True when `userId` signed the FIRST part of the
// form's latest (completed) version. A medical recommendation may only be redone
// — started, accepted, declined or cancelled — by its first signatory (the "lead"
// doctor). For a joint med rec (A3/A7) that is the Part-1 doctor only; the Part-2
// doctor can request a redo but not initiate one. Applications remain team-wide and
// do not use this. Layer B (enforced at sign time) separately pins each replacement
// part to the matching original signatory so the same doctors re-sign in order.
export function isFirstSignatory(form, userId) {
    const latest = form.versions[form.latestVersion - 1];
    return latest?.signatures[0]?.userId === userId;
}
export function formHasSignatures(form) {
    return getLatestVersion(form).signatures?.length;
}
export function formIsMedRec(form) {
    const id = "formTemplate" in form ? form.formTemplate?.id : form.id;
    return medicalRecommendationTemplateIds.includes(id);
}
export function formIsSingleMedRec(form) {
    const id = "formTemplate" in form ? form.formTemplate?.id : form.id;
    return singleMedicalRecordsTemplateIds.includes(id);
}
export function formIsApplication(form) {
    const id = "formTemplate" in form ? form.formTemplate?.id : form.id;
    return applicationTemplateIds.includes(id);
}
export function formIsRecordOfDetention(form) {
    const id = "formTemplate" in form ? form.formTemplate?.id : form.id;
    return recordOfDetentionTemplateIds.includes(id);
}
export function formIsDeMinimisEligible(form) {
    const id = "formTemplate" in form ? form.formTemplate?.id : form.id;
    return deMinimisEligibleTemplateIds.includes(id);
}
export function formIsMca(form) {
    const id = "formTemplate" in form ? form.formTemplate?.id : form.id;
    return mcaTemplateIds.includes(id);
}
export function formIsFdmca(form) {
    const id = "formTemplate" in form ? form.formTemplate?.id : form.id;
    return id === FDMCA_TEMPLATE_ID;
}
export function formIsBid(form) {
    const id = "formTemplate" in form ? form.formTemplate?.id : form.id;
    return id === BID_TEMPLATE_ID;
}
/**
 * A form's first version. Forms are always created with one, so an absent
 * version 1 is corrupt data rather than a case for the caller to handle.
 * Callers that want the form's current data want `getLatestVersion`.
 */
export function getFirstVersion(form) {
    const version = form.versions[0];
    if (version === undefined) {
        throw new Error("Form has no versions");
    }
    return version;
}
export function getLatestVersion(form) {
    return form?.versions[form.latestVersion - 1];
}
/**
 * Determines whether signing a specific part will complete the form.
 * Uses the template's isCompleteFn if available, otherwise checks if it's the last part.
 *
 * @param template - The form template with parts and optional isCompleteFn
 * @param partNumber - The part number being signed (1-based)
 * @param existingPartData - Array of existing signed part data (for parts before partNumber)
 * @param currentPartData - The data for the part being signed
 * @returns true if signing this part will complete the form
 */
export function willFormBeCompleteAfterSigning(template, partNumber, existingPartData, currentPartData) {
    // Build the hypothetical data array as it would be after signing
    const hypotheticalData = [
        ...existingPartData.slice(0, partNumber - 1),
        { data: currentPartData },
    ];
    if (template.isCompleteFn) {
        return template.isCompleteFn(hypotheticalData);
    }
    // Default: form is complete when all parts are signed
    return partNumber === template.parts.length;
}
// A map to allow us to easily reverse linked forms, so that if you retrieve a med rec form,
// you can see that it 'supports' an application, and if you retrieve the application you can
// see it is 'supported-by' the med rec. We will only store the 'supports' link in the DB
export const linkedFormLookup = [
    { reason: "supports", inverseReason: "supported-by", type: "source" },
    { reason: "supported-by", inverseReason: "supports", type: "destination" },
    { reason: "acknowledges", inverseReason: "acknowledged-by", type: "source" },
    {
        reason: "acknowledged-by",
        inverseReason: "acknowledges",
        type: "destination",
    },
];
export const FORM_BANNER_TYPES = [
    "warning",
    "info",
    "error",
    "confirmation",
];
export const errorValidationOutcome = (reason) => ({
    type: "error",
    reason,
});
export const warningValidationOutcome = (reason, isIgnorable = false) => ({
    type: "warning",
    reason,
    isIgnorable,
});
//# sourceMappingURL=form.js.map